import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocSignatures,
  DocContactQR,
  DocInlineDate,
  DocInlineAresName,
  DocInlineAresLookup,
  DocActionButton,
  DraftBanner,
  DocNotes,
  createDocNote,
  type DocNote,
  SavedDocsButton,
  NewDocButton,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
  type DocRowsSnapshot,
} from '../../components/document';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  ContactPickerButton,
  type AresPickResult,
  type CompanyPickResult,
  type ContactPerson,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { useSaveDocument } from '../../api/hooks';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useActiveStavba } from '../stavba-context/StavbaContext';

/**
 * Předávací protokol — dílo (velký).
 * Rozšířená právní varianta dle vzoru přejímacího řízení: smluvní strany,
 * průběh a hodnocení díla, vady a nedodělky, předaná dokumentace, přílohy.
 */

type UcastnikPreset = 'tdi' | 'tds' | 'bozp' | 'autorsky' | 'other';

interface Ucastnik {
  id: string;
  preset: UcastnikPreset;
  strana: string;
  firma: string;
  jmeno: string;
  funkce: string;
  tel: string;
  email: string;
}

const PRESET_LABELS: Record<UcastnikPreset, { strana: string; funkce: string }> = {
  tdi: { strana: 'Technický dozor investora (TDI)', funkce: 'TDI / TDS' },
  tds: { strana: 'Technický dozor stavebníka (TDS)', funkce: 'TDS' },
  bozp: { strana: 'Koordinátor BOZP', funkce: 'Koordinátor BOZP' },
  autorsky: { strana: 'Autorský dozor', funkce: 'Autorský dozor' },
  other: { strana: 'Další účastník', funkce: '' },
};

interface Strana {
  name: string;
  address: string;
  ico: string;
  dic: string;
  osoba: string;
  funkce: string;
  tel: string;
  email: string;
}

const emptyStrana: Strana = {
  name: '',
  address: '',
  ico: '',
  dic: '',
  osoba: '',
  funkce: '',
  tel: '',
  email: '',
};

type DiloVelkyData = {
  cisloZapisu: string;
  datumZahajeni: string;
  nazev: string;
  objednatel: Strana;
  zhotovitel: Strana;
  predmet: string;
  spCj: string;
  spDatum: string;
  sodCislo: string;
  zahajeni: string;
  ukonceni: string;
  zarukaDo: string;
  cenaSod: string;
  duvodyLhut: string;
  odchylky: string;
  jakost: string;
  vadyText: string;
  dohodaTermin: string;
  dohodaPristup: string;
  dodatecnePrace: string;
  terminVyklizeni: string;
  dalsiUjednani: string;
  dokumentace: string;
  prilohy: string;
  datumSkonceni: string;
  signatures: {
    zhotovitel: string;
    objednatel: string;
    ucastnici: Record<string, string>;
  };
  ucastnici: Ucastnik[];
  /** Volitelné textové rámečky sekce „Ostatní". */
  ostatni: DocNote[];
};

const initialData: DiloVelkyData = {
  cisloZapisu: '',
  datumZahajeni: '',
  nazev: '',
  objednatel: { ...emptyStrana },
  zhotovitel: { ...emptyStrana },
  predmet: '',
  spCj: '',
  spDatum: '',
  sodCislo: '',
  zahajeni: '',
  ukonceni: '',
  zarukaDo: '',
  cenaSod: '',
  duvodyLhut: '',
  odchylky: '',
  jakost: '',
  vadyText: '',
  dohodaTermin: '',
  dohodaPristup: '',
  dodatecnePrace: '',
  terminVyklizeni: '',
  dalsiUjednani: '',
  dokumentace: '',
  prilohy: '',
  datumSkonceni: '',
  signatures: { zhotovitel: '', objednatel: '', ucastnici: {} },
  ucastnici: [],
  ostatni: [],
};

function newUcastnikId(): string {
  return `u-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function createUcastnik(preset: UcastnikPreset): Ucastnik {
  const p = PRESET_LABELS[preset];
  return {
    id: newUcastnikId(),
    preset,
    strana: p.strana,
    funkce: p.funkce,
    firma: '',
    jmeno: '',
    tel: '',
    email: '',
  };
}

/** Snímek do konceptu / `data_json`: data + skryté a vlastní řádky hlavičkových sekcí. */
type DiloVelkyDraft = DiloVelkyData & { radky?: DocRowsSnapshot };

export function ProtocolDiloVelkyPage() {
  const [data, setData] = useState<DiloVelkyData>(initialData);
  const [emailOpen, setEmailOpen] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const { activeStavba } = useActiveStavba();

  // Skryté standardní řádky (✕ / „obnovit") + vlastní řádky („+ Řádek") v hlavičkových sekcích.
  const rows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = rows;

  const setSignature = (who: 'zhotovitel' | 'objednatel', dataUrl: string) =>
    setData((d) => ({ ...d, signatures: { ...d.signatures, [who]: dataUrl } }));
  const setUcastnikSignature = (id: string, dataUrl: string) =>
    setData((d) => ({
      ...d,
      signatures: {
        ...d.signatures,
        ucastnici: { ...d.signatures.ucastnici, [id]: dataUrl },
      },
    }));

  const { editId } = useDocEditor((raw) => {
    const { radky, ...rest } = raw;
    setData((prev) => ({ ...prev, ...(rest as Partial<DiloVelkyData>) }));
    rows.apply(radky);
  });

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nového formuláře).
  const draft = useDocDraft<DiloVelkyDraft>('predavaci-dilo-velky', editId == null);
  useEffect(() => {
    draft.persist({ ...data, radky: rows.snapshot() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, rows]);

  function restoreDraft() {
    if (!draft.pending) return;
    const { radky, ...rest } = draft.pending.data;
    setData((prev) => ({ ...prev, ...(rest as Partial<DiloVelkyData>) }));
    rows.apply(radky);
    draft.accept();
  }

  async function handleSave() {
    try {
      await saveDoc.mutateAsync({
        id: editId ?? undefined,
        doc_type: 'predavaci-dilo-velky',
        title: `Předávací protokol díla (velký) ${data.sodCislo || data.nazev}`,
        stavba: activeStavba?.nazev ?? '',
        data_json: { ...data, radky: rows.snapshot() },
      });
      draft.clear();
      toast.success('Dokument uložen do archivu.');
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
    }
  }

  // Podtitulek pod nadpisem — jen z vyplněných částí (jinak by zbylo „č.  ·").
  const cisloValue = isHidden('s01.cislo') ? '' : data.cisloZapisu.trim();
  const sodValue = isHidden('s05.sod') ? '' : data.sodCislo.trim();
  const docMeta = [
    (cisloValue || sodValue) && `č. ${cisloValue || sodValue}`,
    data.datumSkonceni.trim(),
  ]
    .filter(Boolean)
    .join(' · ');

  const filled = useMemo(() => {
    const allValues = [
      data.cisloZapisu, data.datumZahajeni, data.nazev,
      data.objednatel.name, data.objednatel.address, data.objednatel.ico,
      data.zhotovitel.name, data.zhotovitel.address, data.zhotovitel.ico,
      data.predmet, data.sodCislo, data.zahajeni, data.ukonceni, data.cenaSod,
      data.datumSkonceni,
    ];
    const total = allValues.length;
    const filledCount = allValues.filter((v) => v && v.trim().length > 0).length;
    return Math.round((filledCount / total) * 100);
  }, [data]);

  const set = <K extends keyof DiloVelkyData>(key: K, value: DiloVelkyData[K]) =>
    setData((d) => ({ ...d, [key]: value }));
  const setObjednatel = (patch: Partial<Strana>) =>
    setData((d) => ({ ...d, objednatel: { ...d.objednatel, ...patch } }));
  const setZhotovitel = (patch: Partial<Strana>) =>
    setData((d) => ({ ...d, zhotovitel: { ...d.zhotovitel, ...patch } }));

  const addUcastnik = (preset: UcastnikPreset) =>
    setData((d) => ({ ...d, ucastnici: [...d.ucastnici, createUcastnik(preset)] }));
  const updateUcastnik = (id: string, patch: Partial<Ucastnik>) =>
    setData((d) => ({
      ...d,
      ucastnici: d.ucastnici.map((u) => (u.id === id ? { ...u, ...patch } : u)),
    }));
  const removeUcastnik = (id: string) =>
    setData((d) => ({ ...d, ucastnici: d.ucastnici.filter((u) => u.id !== id) }));

  // Volitelná sekce „Ostatní" — libovolný počet textových rámečků.
  const addOstatni = () => setData((d) => ({ ...d, ostatni: [...d.ostatni, createDocNote()] }));
  const updateOstatni = (id: string, patch: Partial<DocNote>) =>
    setData((d) => ({
      ...d,
      ostatni: d.ostatni.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    }));
  const removeOstatni = (id: string) =>
    setData((d) => ({ ...d, ostatni: d.ostatni.filter((n) => n.id !== id) }));

  const addUcastnikActions = (
    <>
      <DocActionButton onClick={() => addUcastnik('tdi')}>+ TDI</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('tds')}>+ TDS</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('bozp')}>+ BOZP</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('autorsky')}>+ Autorský</DocActionButton>
      <DocActionButton onClick={() => addUcastnik('other')}>+ Vlastní</DocActionButton>
    </>
  );

  return (
    <>
      <DocLayout
        breadcrumb={['Předání', 'Dílo (velký)']}
        title="Protokol o předání díla — rozšířený"
        statusText="Rozpracovaný koncept"
        status="draft"
        formProgress={{ percent: filled, label: 'Vyplněno' }}
        actions={
          <>
            <NewDocButton draftKey="predavaci-dilo-velky" path="/app/predavaci-dilo-velky" />
            <SavedDocsButton
              docType="predavaci-dilo-velky"
              title="Uložené předávací protokoly — dílo (velký)"
              currentId={editId}
            />
            <Button
              variant="secondary"
              leading="⎙"
              onClick={() =>
                printToPdf(
                  `Predavaci_protokol_dilo_velky_${data.sodCislo || data.nazev || 'dilo'}.pdf`,
                )
              }
            >
              PDF
            </Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: 'SN',
              name: data.zhotovitel.name,
              address: `${data.zhotovitel.address || 'Adresa'} · IČO ${data.zhotovitel.ico}`,
            }}
            tag="Předávací protokol — dílo (velký)"
            title="Protokol o předání a převzetí díla"
            meta={docMeta}
            footer={{ left: data.zhotovitel.name, center: 'str. 1 / 1', right: sodValue }}
          >
            <DocBlock
              idx="01"
              title="Záhlaví protokolu"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s01" />
                  <StavbaPickerButton
                    title="Načíst stavbu z adresáře"
                    onPick={(s: StavbaPickResult) =>
                      setData((d) => ({
                        ...d,
                        nazev: s.nazev,
                        spCj: s.stavebni_povoleni || d.spCj,
                        sodCislo: s.zakazka_cislo || d.sodCislo,
                      }))
                    }
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu do adresáře"
                    build={() => ({
                      nazev: data.nazev,
                      adresa: data.objednatel.address,
                      stavebni_povoleni: data.spCj,
                      zakazka_cislo: data.sodCislo,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s01.cislo') && (
                <DocRow
                  k="Číslo zápisu"
                  mono
                  value={data.cisloZapisu}
                  onChange={(v) => set('cisloZapisu', v)}
                  placeholder="např. PP-2026/0007"
                  onRemove={() => hide('s01.cislo')}
                />
              )}
              {!isHidden('s01.zahajeniRizeni') && (
                <DocRow
                  k="Datum zahájení přejímacího řízení"
                  mono
                  value={data.datumZahajeni}
                  onChange={(v) => set('datumZahajeni', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.datumZahajeni}
                      onChange={(v) => set('datumZahajeni', v)}
                    />
                  }
                  onRemove={() => hide('s01.zahajeniRizeni')}
                />
              )}
              {!isHidden('s01.nazev') && (
                <DocRow
                  k="Název stavby / díla"
                  value={data.nazev}
                  onChange={(v) => set('nazev', v)}
                  placeholder="např. Novostavba RD…"
                  onRemove={() => hide('s01.nazev')}
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Objednatel (přejímající)"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s02" />
                  <CompanyPickerButton
                    preferRole="objednatel"
                    title="Načíst objednatele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setObjednatel({
                        name: c.nazev,
                        ico: c.ico,
                        dic: c.dic,
                        address: c.adresa,
                        osoba: c.kontakt_jmeno,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit objednatele do adresáře"
                    build={() => ({
                      role: 'objednatel',
                      nazev: data.objednatel.name,
                      ico: data.objednatel.ico,
                      dic: data.objednatel.dic,
                      adresa: data.objednatel.address,
                      kontakt_jmeno: data.objednatel.osoba,
                      kontakt_tel: data.objednatel.tel,
                      kontakt_email: data.objednatel.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s02.firma') && (
                <DocRow
                  k="Název / Jméno"
                  v={
                    <DocInlineAresName
                      value={data.objednatel.name}
                      onChange={(v) => setObjednatel({ name: v })}
                      placeholder="jméno objednatele / firma"
                      onPick={(d: AresPickResult) =>
                        setObjednatel({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s02.firma')}
                />
              )}
              {!isHidden('s02.adresa') && (
                <DocRow
                  k="Sídlo / Adresa"
                  value={data.objednatel.address}
                  onChange={(v) => setObjednatel({ address: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={data.objednatel.address}
                      onApply={(v) => setObjednatel({ address: v })}
                      title="Adresa objednatele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s02.adresa')}
                />
              )}
              {!isHidden('s02.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={data.objednatel.ico}
                  onChange={(v) => setObjednatel({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={data.objednatel.ico}
                      onResult={(d) =>
                        setObjednatel({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s02.ico')}
                />
              )}
              {!isHidden('s02.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={data.objednatel.dic}
                  onChange={(v) => setObjednatel({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s02.dic')}
                />
              )}
              {!isHidden('s02.osoba') && (
                <DocRow
                  k="Zastoupený / kontakt"
                  value={data.objednatel.osoba}
                  onChange={(v) => setObjednatel({ osoba: v })}
                  placeholder="jméno – vyplňte nebo vyberte z adresáře"
                  actions={
                    <ContactPickerButton
                      attachToCompany={data.objednatel.name}
                      companyDraft={{ role: 'objednatel', ico: data.objednatel.ico, adresa: data.objednatel.address }}
                      draftNew={{
                        jmeno: data.objednatel.osoba,
                        tel: data.objednatel.tel,
                        email: data.objednatel.email,
                      }}
                      title="Zástupce objednatele — z adresáře"
                      onPick={(p: ContactPerson) =>
                        setObjednatel({
                          osoba: p.jmeno,
                          ...(p.funkce ? { funkce: p.funkce } : {}),
                          ...(p.tel ? { tel: p.tel } : {}),
                          ...(p.email ? { email: p.email } : {}),
                        })
                      }
                    />
                  }
                  onRemove={() => hide('s02.osoba')}
                />
              )}
              {!isHidden('s02.funkce') && (
                <DocRow
                  k="Funkce"
                  value={data.objednatel.funkce}
                  onChange={(v) => setObjednatel({ funkce: v })}
                  placeholder="např. jednatel"
                  onRemove={() => hide('s02.funkce')}
                />
              )}
              {!isHidden('s02.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={data.objednatel.tel}
                  onChange={(v) => setObjednatel({ tel: v })}
                  inputType="tel"
                  placeholder="+420 …"
                  onRemove={() => hide('s02.tel')}
                />
              )}
              {!isHidden('s02.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={data.objednatel.email}
                  onChange={(v) => setObjednatel({ email: v })}
                  inputType="email"
                  placeholder="email@…"
                  actions={
                    <DocContactQR
                      name={data.objednatel.osoba || data.objednatel.name}
                      role="Objednatel"
                      org={data.objednatel.name}
                      phone={data.objednatel.tel}
                      email={data.objednatel.email}
                      title="QR vizitka — objednatel"
                    />
                  }
                  onRemove={() => hide('s02.email')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            <DocBlock
              idx="03"
              title="Zhotovitel (předávající)"
              hiddenCount={hiddenCount('s03')}
              onRestoreHidden={() => restoreSection('s03')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s03" />
                  <CompanyPickerButton
                    preferRole="zhotovitel"
                    title="Načíst zhotovitele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setZhotovitel({
                        name: c.nazev,
                        ico: c.ico,
                        dic: c.dic,
                        address: c.adresa,
                        osoba: c.stavbyvedouci || c.kontakt_jmeno,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit zhotovitele do adresáře"
                    build={() => ({
                      role: 'zhotovitel',
                      nazev: data.zhotovitel.name,
                      ico: data.zhotovitel.ico,
                      dic: data.zhotovitel.dic,
                      adresa: data.zhotovitel.address,
                      kontakt_jmeno: data.zhotovitel.osoba,
                      kontakt_tel: data.zhotovitel.tel,
                      kontakt_email: data.zhotovitel.email,
                      stavbyvedouci: data.zhotovitel.osoba,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s03.firma') && (
                <DocRow
                  k="Firma"
                  v={
                    <DocInlineAresName
                      value={data.zhotovitel.name}
                      onChange={(v) => setZhotovitel({ name: v })}
                      placeholder="např. Stavby Novák s.r.o."
                      onPick={(d: AresPickResult) =>
                        setZhotovitel({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.firma')}
                />
              )}
              {!isHidden('s03.adresa') && (
                <DocRow
                  k="Sídlo"
                  value={data.zhotovitel.address}
                  onChange={(v) => setZhotovitel({ address: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={data.zhotovitel.address}
                      onApply={(v) => setZhotovitel({ address: v })}
                      title="Sídlo zhotovitele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s03.adresa')}
                />
              )}
              {!isHidden('s03.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={data.zhotovitel.ico}
                  onChange={(v) => setZhotovitel({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={data.zhotovitel.ico}
                      onResult={(d) =>
                        setZhotovitel({ name: d.nazev, ico: d.ico, address: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.ico')}
                />
              )}
              {!isHidden('s03.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={data.zhotovitel.dic}
                  onChange={(v) => setZhotovitel({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s03.dic')}
                />
              )}
              {!isHidden('s03.osoba') && (
                <DocRow
                  k="Zastoupený / stavbyvedoucí"
                  value={data.zhotovitel.osoba}
                  onChange={(v) => setZhotovitel({ osoba: v })}
                  placeholder="jméno – vyplňte nebo vyberte z adresáře"
                  actions={
                    <ContactPickerButton
                      attachToCompany={data.zhotovitel.name}
                      companyDraft={{ role: 'zhotovitel', ico: data.zhotovitel.ico, adresa: data.zhotovitel.address }}
                      draftNew={{
                        jmeno: data.zhotovitel.osoba,
                        funkce: 'stavbyvedoucí',
                        tel: data.zhotovitel.tel,
                        email: data.zhotovitel.email,
                      }}
                      title="Stavbyvedoucí — z adresáře"
                      onPick={(p: ContactPerson) =>
                        setZhotovitel({
                          osoba: p.jmeno,
                          ...(p.funkce ? { funkce: p.funkce } : {}),
                          ...(p.tel ? { tel: p.tel } : {}),
                          ...(p.email ? { email: p.email } : {}),
                        })
                      }
                    />
                  }
                  onRemove={() => hide('s03.osoba')}
                />
              )}
              {!isHidden('s03.funkce') && (
                <DocRow
                  k="Funkce"
                  value={data.zhotovitel.funkce}
                  onChange={(v) => setZhotovitel({ funkce: v })}
                  placeholder="např. stavbyvedoucí"
                  onRemove={() => hide('s03.funkce')}
                />
              )}
              {!isHidden('s03.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={data.zhotovitel.tel}
                  onChange={(v) => setZhotovitel({ tel: v })}
                  inputType="tel"
                  placeholder="+420 …"
                  onRemove={() => hide('s03.tel')}
                />
              )}
              {!isHidden('s03.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={data.zhotovitel.email}
                  onChange={(v) => setZhotovitel({ email: v })}
                  inputType="email"
                  placeholder="email@…"
                  actions={
                    <DocContactQR
                      name={data.zhotovitel.osoba || data.zhotovitel.name}
                      role="Zhotovitel"
                      org={data.zhotovitel.name}
                      phone={data.zhotovitel.tel}
                      email={data.zhotovitel.email}
                      title="QR vizitka — zhotovitel"
                    />
                  }
                  onRemove={() => hide('s03.email')}
                />
              )}
              <DocCustomRows rows={rows} section="s03" />
            </DocBlock>

            <TextBlock
              idx="04"
              title="Předmět díla"
              rows={3}
              value={data.predmet}
              onChange={(v) => set('predmet', v)}
              placeholder="Stručný popis předmětu díla dle smlouvy o dílo…"
            />

            <DocBlock
              idx="05"
              title="Smlouva o dílo a termíny"
              hiddenCount={hiddenCount('s05')}
              onRestoreHidden={() => restoreSection('s05')}
              actions={<DocAddRowButton rows={rows} section="s05" />}
            >
              {!isHidden('s05.spCj') && (
                <DocRow
                  k="Stavební povolení č.j."
                  mono
                  value={data.spCj}
                  onChange={(v) => set('spCj', v)}
                  placeholder="např. MÚ-2026/0341"
                  onRemove={() => hide('s05.spCj')}
                />
              )}
              {!isHidden('s05.spDatum') && (
                <DocRow
                  k="Stavební povolení vydáno dne"
                  mono
                  value={data.spDatum}
                  onChange={(v) => set('spDatum', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.spDatum}
                      onChange={(v) => set('spDatum', v)}
                    />
                  }
                  onRemove={() => hide('s05.spDatum')}
                />
              )}
              {!isHidden('s05.sod') && (
                <DocRow
                  k="Smlouva o dílo č."
                  mono
                  value={data.sodCislo}
                  onChange={(v) => set('sodCislo', v)}
                  placeholder="např. SoD-2026/0412"
                  onRemove={() => hide('s05.sod')}
                />
              )}
              {!isHidden('s05.zahajeni') && (
                <DocRow
                  k="Zahájení prací dle SOD / skutečné"
                  value={data.zahajeni}
                  onChange={(v) => set('zahajeni', v)}
                  placeholder="dle SOD … / skutečně …"
                  onRemove={() => hide('s05.zahajeni')}
                />
              )}
              {!isHidden('s05.ukonceni') && (
                <DocRow
                  k="Ukončení prací dle SOD / skutečné"
                  value={data.ukonceni}
                  onChange={(v) => set('ukonceni', v)}
                  placeholder="dle SOD … / skutečně …"
                  onRemove={() => hide('s05.ukonceni')}
                />
              )}
              {!isHidden('s05.zaruka') && (
                <DocRow
                  k="Záruční doba (konec)"
                  mono
                  value={data.zarukaDo}
                  onChange={(v) => set('zarukaDo', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.zarukaDo}
                      onChange={(v) => set('zarukaDo', v)}
                    />
                  }
                  onRemove={() => hide('s05.zaruka')}
                />
              )}
              {!isHidden('s05.cena') && (
                <DocRow
                  k="Cena díla dle SOD (bez DPH)"
                  value={data.cenaSod}
                  onChange={(v) => set('cenaSod', v)}
                  placeholder="např. 1 250 000 Kč bez DPH"
                  onRemove={() => hide('s05.cena')}
                />
              )}
              <DocCustomRows rows={rows} section="s05" />
            </DocBlock>

            <TextBlock
              idx="06"
              title="Důvody nedodržení lhůt"
              value={data.duvodyLhut}
              onChange={(v) => set('duvodyLhut', v)}
              placeholder="Pokud nebyly lhůty dodrženy, uveďte důvody…"
            />

            <TextBlock
              idx="07"
              title="Odchylky od projektu"
              value={data.odchylky}
              onChange={(v) => set('odchylky', v)}
              placeholder="Provedené odchylky od PD a jejich odsouhlasení…"
            />

            <TextBlock
              idx="08"
              title="Zhodnocení jakosti"
              value={data.jakost}
              onChange={(v) => set('jakost', v)}
              placeholder="Celkové zhodnocení jakosti provedeného díla…"
            />

            <TextBlock
              idx="09"
              title="Soupis vad a nedodělků"
              rows={4}
              value={data.vadyText}
              onChange={(v) => set('vadyText', v)}
              placeholder="Seznam vad a nedodělků zjištěných při přejímce…"
            />

            <DocBlock
              idx="10"
              title="Dohody a termíny k vadám"
              hiddenCount={hiddenCount('s10')}
              onRestoreHidden={() => restoreSection('s10')}
            >
              {!isHidden('s10.dohodaTermin') && (
                <DocRow
                  k="Dohoda o termínu odstranění"
                  value={data.dohodaTermin}
                  onChange={(v) => set('dohodaTermin', v)}
                  placeholder="Dohodnutý termín odstranění vad a nedodělků…"
                  onRemove={() => hide('s10.dohodaTermin')}
                />
              )}
              {!isHidden('s10.dohodaPristup') && (
                <DocRow
                  k="Dohoda o přístupu zhotovitele"
                  value={data.dohodaPristup}
                  onChange={(v) => set('dohodaPristup', v)}
                  placeholder="Podmínky přístupu zhotovitele k odstranění vad…"
                  onRemove={() => hide('s10.dohodaPristup')}
                />
              )}
              {!isHidden('s10.vyklizeni') && (
                <DocRow
                  k="Termín vyklizení staveniště"
                  mono
                  value={data.terminVyklizeni}
                  onChange={(v) => set('terminVyklizeni', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.terminVyklizeni}
                      onChange={(v) => set('terminVyklizeni', v)}
                    />
                  }
                  onRemove={() => hide('s10.vyklizeni')}
                />
              )}
            </DocBlock>

            <TextBlock
              idx="11"
              title="Dodatečně požadované práce"
              value={data.dodatecnePrace}
              onChange={(v) => set('dodatecnePrace', v)}
              placeholder="Práce požadované objednatelem nad rámec SOD…"
            />

            <TextBlock
              idx="12"
              title="Další ujednání"
              value={data.dalsiUjednani}
              onChange={(v) => set('dalsiUjednani', v)}
              placeholder="Další dohody mezi stranami…"
            />

            <TextBlock
              idx="13"
              title="Předaná dokumentace"
              value={data.dokumentace}
              onChange={(v) => set('dokumentace', v)}
              placeholder="PD skut. provedení, revize, atesty, návody, záruční listy…"
            />

            <TextBlock
              idx="14"
              title="Přílohy"
              value={data.prilohy}
              onChange={(v) => set('prilohy', v)}
              placeholder="Fotodokumentace, protokoly o zkouškách, geometrický plán…"
            />

            <DocBlock
              idx="15"
              title="Skončení přejímacího řízení"
              hiddenCount={hiddenCount('s15')}
              onRestoreHidden={() => restoreSection('s15')}
            >
              {!isHidden('s15.datum') && (
                <DocRow
                  k="Datum skončení přejímacího řízení"
                  mono
                  value={data.datumSkonceni}
                  onChange={(v) => set('datumSkonceni', v)}
                  placeholder="dd. mm. rrrr"
                  actions={
                    <DocInlineDate
                      value={data.datumSkonceni}
                      onChange={(v) => set('datumSkonceni', v)}
                    />
                  }
                  onRemove={() => hide('s15.datum')}
                />
              )}
            </DocBlock>

            <DocBlock idx="16" title="Další účastníci" actions={addUcastnikActions}>
              {data.ucastnici.length === 0 ? (
                <DocRow
                  k="—"
                  v={
                    <span style={{ color: 'var(--sd-doc-dim)', fontStyle: 'italic' }}>
                      Žádní další účastníci. Přidej je tlačítky vpravo.
                    </span>
                  }
                />
              ) : (
                data.ucastnici.map((u, i) => (
                  <UcastnikRows
                    key={u.id}
                    u={u}
                    index={i}
                    onUpdate={(patch) => updateUcastnik(u.id, patch)}
                    onRemove={() => removeUcastnik(u.id)}
                  />
                ))
              )}
            </DocBlock>

            {data.ostatni.length > 0 ? (
              <DocBlock
                idx="17"
                title="Ostatní"
                actions={
                  <DocActionButton onClick={addOstatni}>+ Přidat rámeček</DocActionButton>
                }
              >
                <DocNotes items={data.ostatni} onChange={updateOstatni} onRemove={removeOstatni} />
              </DocBlock>
            ) : (
              <div className="docNoPrint" style={{ marginTop: 14 }}>
                <DocActionButton
                  onClick={addOstatni}
                  title="Volitelná sekce — poznámky, připomínky, vyjádření TDS…"
                >
                  + Přidat sekci „Ostatní"
                </DocActionButton>
              </div>
            )}

            <DocSignatures
              items={[
                {
                  label: 'Zhotovitel (předávající)',
                  name: data.zhotovitel.osoba || data.zhotovitel.name,
                  role: data.zhotovitel.funkce || 'předávající',
                  image: data.signatures.zhotovitel,
                  onSign: (v) => setSignature('zhotovitel', v),
                },
                {
                  label: 'Objednatel (přejímající)',
                  name: data.objednatel.osoba || data.objednatel.name,
                  role: data.objednatel.funkce || 'přejímající',
                  image: data.signatures.objednatel,
                  onSign: (v) => setSignature('objednatel', v),
                },
                ...data.ucastnici.map((u) => ({
                  label: u.strana || 'Další účastník',
                  name: u.jmeno || '—',
                  role: u.funkce || '',
                  image: data.signatures.ucastnici[u.id] || '',
                  onSign: (v: string) => setUcastnikSignature(u.id, v),
                })),
              ]}
            />
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="predavaci-dilo-velky"
        docTitle={`Předávací protokol díla — ${data.nazev || 'dílo'}`}
        defaultTo={data.objednatel.email}
        defaultCc={data.zhotovitel.email}
        defaultSubject={`Předávací protokol díla — ${data.nazev}${sodValue ? ' (SoD č. ' + sodValue + ')' : ''}`}
        meta={`Stavba: ${data.nazev || '—'}${sodValue ? ' · SoD: ' + sodValue : ''}${activeStavba?.nazev ? ' · ' + activeStavba.nazev : ''}`}
      />
    </>
  );
}

/**
 * Blok s jediným dlouhým textovým polem. Multiline DocRow popisek skrývá
 * (řádek je na celou šířku), takže popiskem je samotný titulek bloku.
 */
function TextBlock({
  idx,
  title,
  value,
  onChange,
  placeholder,
  rows = 2,
}: {
  idx: string;
  title: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  rows?: number;
}) {
  return (
    <DocBlock idx={idx} title={title}>
      <DocRow
        k={title}
        multiline
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </DocBlock>
  );
}

/** Účastník vykreslený přímo jako sekce řádků v dokumentu. */
function UcastnikRows({
  u,
  index,
  onUpdate,
  onRemove,
}: {
  u: Ucastnik;
  index: number;
  onUpdate: (patch: Partial<Ucastnik>) => void;
  onRemove: () => void;
}) {
  const preferRole =
    u.preset === 'tdi' || u.preset === 'tds'
      ? 'tds'
      : u.preset === 'bozp'
        ? 'bozp_koordinator'
        : u.preset === 'autorsky'
          ? 'projektant'
          : undefined;

  const saveRole =
    u.preset === 'bozp'
      ? 'bozp_koordinator'
      : u.preset === 'tdi' || u.preset === 'tds'
        ? 'tds'
        : u.preset === 'autorsky'
          ? 'projektant'
          : 'subdodavatel';

  return (
    <>
      <DocRow
        k={`Účastník ${index + 1}`}
        value={u.strana}
        onChange={(v) => onUpdate({ strana: v })}
        placeholder="Strana / role"
        actions={
          <>
            <CompanyPickerButton
              preferRole={preferRole}
              title={`Načíst ${u.strana} z adresáře`}
              onPick={(c) =>
                onUpdate({
                  firma: c.nazev,
                  jmeno: c.kontakt_jmeno || c.stavbyvedouci || u.jmeno,
                  tel: c.kontakt_tel,
                  email: c.kontakt_email,
                })
              }
            />
            <SaveCompanyButton
              title={`Uložit ${u.strana} do adresáře`}
              build={() => ({
                role: saveRole,
                nazev: u.firma || u.strana,
                kontakt_jmeno: u.jmeno,
                kontakt_tel: u.tel,
                kontakt_email: u.email,
                bozp_koordinator: u.preset === 'bozp' ? u.jmeno : '',
                bozp_tel: u.preset === 'bozp' ? u.tel : '',
                poznamka: u.funkce,
              })}
            />
            <DocActionButton onClick={onRemove} title="Odebrat účastníka">
              ✕
            </DocActionButton>
          </>
        }
      />
      <DocRow
        k="Firma"
        value={u.firma}
        onChange={(v) => onUpdate({ firma: v })}
        placeholder="název firmy"
      />
      <DocRow
        k="Jméno a příjmení"
        value={u.jmeno}
        onChange={(v) => onUpdate({ jmeno: v })}
        placeholder="jméno – vyplňte nebo vyberte z adresáře"
      />
      <DocRow
        k="Funkce"
        value={u.funkce}
        onChange={(v) => onUpdate({ funkce: v })}
        placeholder="funkce"
      />
      <DocRow
        k="Telefon"
        mono
        value={u.tel}
        onChange={(v) => onUpdate({ tel: v })}
        inputType="tel"
        placeholder="+420 …"
      />
      <DocRow
        k="E-mail"
        mono
        value={u.email}
        onChange={(v) => onUpdate({ email: v })}
        inputType="email"
        placeholder="email@…"
        actions={
          <DocContactQR
            name={u.jmeno}
            role={u.funkce || u.strana}
            org={u.firma}
            phone={u.tel}
            email={u.email}
            compact
            label="QR"
            title={`QR vizitka — ${u.strana}`}
          />
        }
      />
    </>
  );
}
